// ADO-1779 — Back to Top (Figma component 5997:2165; placed on the Order Review mobile frame
// 5997:50532 and tablet frame 5997:51424). The Figma note: a floating button for long lists on
// mobile and tablet that appears once the user has scrolled about two screens down and scrolls
// smoothly to the top, named "Back to top".
//
// It follows the window's scroll (every page here scrolls the document, not an inner box) and
// shows the button only once the window is more than two viewport heights down. The CSS hides it
// at desktop widths (≥1024px) and lifts it over the mobile tab bar. Reduced motion jumps instead
// of animating. The button leaves the page once the top is reached, so activating it hands
// focus to `focusTargetId` (the page's h1) rather than dropping it to <body>.
//
// The fixed button would otherwise sit over the page's last control once the Shopper reaches the
// end, so the component also renders an empty, always-present reserve in the page flow: mount it
// as the last child of the page's content column.
import { useEffect, useState } from 'react';
import { Icon } from '../Icon/Icon';
import { prefersReducedMotion } from '../motion';
import styles from './BackToTop.module.css';

/** How many viewport heights the window must be scrolled before the button shows. */
export const BACK_TO_TOP_SCREENS = 2;

export interface BackToTopProps {
  /** The id of the element that takes focus once the button is used (the page's h1). */
  readonly focusTargetId?: string;
}

function scrolledPastThreshold(): boolean {
  return window.scrollY > window.innerHeight * BACK_TO_TOP_SCREENS;
}

export function BackToTop({ focusTargetId }: BackToTopProps) {
  const [shown, setShown] = useState(scrolledPastThreshold);

  useEffect(() => {
    const update = () => setShown(scrolledPastThreshold());
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const backToTop = () => {
    window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    if (focusTargetId !== undefined) {
      document.getElementById(focusTargetId)?.focus({ preventScroll: true });
    }
  };

  return (
    <>
      <div className={styles.reserve} aria-hidden="true" data-testid="back-to-top-reserve" />
      {shown ? (
        <button
          type="button"
          className={styles.button}
          aria-label="Back to top"
          onClick={backToTop}
        >
          <Icon name="arrowUp" size={20} />
        </button>
      ) : null}
    </>
  );
}
